import { get, ref, onValue } from 'firebase/database';
import { database } from '../firebaseConfig';
import { TournamentMatchContext, TournamentMatchResult } from '../types';
import {
  completeTournamentPoolMatch,
  completeTournamentPlayoffMatch,
  confirmTournamentMatchSaved,
  normalizeTournament,
} from './tournamentService';

/**
 * Durable outbox for tournament match results.
 *
 * The web Firebase SDK keeps unsent writes in MEMORY only — close the tab,
 * reload, or let the phone kill the page, and a result queued on bad wifi is
 * gone with no way to retry it. That is how results were lost at a live
 * tournament and had to be awarded by hand.
 *
 * So the result is written to localStorage BEFORE the network is touched, and
 * only removed once the server has confirmed it. Everything else — retry on
 * reconnect, retry on next app start, the "unsent result" banner — reads from
 * here.
 *
 * Deliberately NOT stored: the tournament snapshot. It can be large and, more
 * importantly, stale; the flush re-reads the tournament fresh, which is the
 * same rule the completion path follows for advancement.
 */

const KEY = 'tf_result_outbox_v1';

export interface PendingResult {
  id: string;
  ctx: TournamentMatchContext;
  result: TournamentMatchResult;
  /** Display only — who this result was between, for the banner. */
  label: string;
  /**
   * The two players this result was recorded for.
   *
   * A queued result is NOT a recorded one, so the regeneration guards do not
   * see it and a bracket can legitimately be rebuilt while it is still in
   * flight. Without this check the flush would then force-write an old result
   * into whatever match now occupies that round/index — completing a game
   * between two different people, with a third player as the winner, and
   * advancing them. Cheap insurance against a genuinely chaotic sequence.
   */
  playerUids?: string[];
  queuedAt: number;
  attempts: number;
  lastError?: string;
}

const read = (): PendingResult[] => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (items: PendingResult[]): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* storage full or blocked — the in-flight save still runs */
  }
};

const listeners = new Set<(items: PendingResult[]) => void>();
const emit = () => {
  const items = read();
  listeners.forEach((l) => l(items));
};

export const subscribeOutbox = (cb: (items: PendingResult[]) => void): (() => void) => {
  listeners.add(cb);
  cb(read());
  return () => {
    listeners.delete(cb);
  };
};

export const outboxKeyFor = (ctx: TournamentMatchContext): string =>
  ctx.phase === 'pool'
    ? `${ctx.tournamentId}:pool:${ctx.matchId}`
    : `${ctx.tournamentId}:playoff:${ctx.roundIndex}:${ctx.matchIndex}`;

/** Queue a result. Re-queuing the same match replaces the earlier entry. */
export const enqueueResult = (
  ctx: TournamentMatchContext,
  result: TournamentMatchResult,
  label: string,
  playerUids?: string[]
): string => {
  const id = outboxKeyFor(ctx);
  const items = read().filter((i) => i.id !== id);
  items.push({ id, ctx, result, label, playerUids, queuedAt: Date.now(), attempts: 0 });
  write(items);
  emit();
  return id;
};

export const removeResult = (id: string): void => {
  write(read().filter((i) => i.id !== id));
  emit();
};

export const listPending = (): PendingResult[] => read();

let flushing = false;

/**
 * Try to land every queued result.
 *
 * Uses the same force + server-confirm path as a manual RETRY: `force` gets
 * past an optimistic copy sitting in the SDK's local cache, and the read-back
 * is what actually proves the backend has it. An entry is only dropped once
 * confirmed.
 */
export const flushOutbox = async (): Promise<{ sent: number; remaining: number }> => {
  if (flushing) return { sent: 0, remaining: read().length };
  flushing = true;
  let sent = 0;
  try {
    for (const item of read()) {
      try {
        const snap = await get(ref(database, `tournaments/${item.ctx.tournamentId}`));
        if (!snap.exists()) {
          // The tournament is gone — nothing to save into.
          removeResult(item.id);
          continue;
        }
        const tournament = normalizeTournament(item.ctx.tournamentId, snap.val());

        // Refuse to write into a slot now occupied by different players.
        if (item.playerUids?.length === 2) {
          const live =
            item.ctx.phase === 'pool'
              ? tournament.poolMatches?.[item.ctx.matchId]
              : tournament.playoffRounds?.[`${item.ctx.roundIndex}`]?.matches?.[`${item.ctx.matchIndex}`];
          const liveUids = [live?.player1?.uid, live?.player2?.uid].filter(Boolean) as string[];
          const sameMatch =
            liveUids.length === 2 && item.playerUids.every((uid) => liveUids.includes(uid));
          if (liveUids.length && !sameMatch) {
            bumpAttempt(
              item.id,
              'The bracket changed — this result no longer matches the players in that slot. Ask the organiser to enter it manually.'
            );
            continue;
          }
        }

        // The outbox exists to guarantee the RESULT reaches the server. Writing
        // it is only the first half of completion — propagation (advancement,
        // champion) follows, and that half can legitimately fail on its own:
        // declaring a champion is denied outright for a device signed in as
        // someone who is not a participant of this tournament.
        //
        // So a throw here does NOT mean the result is missing. Confirm either
        // way. Getting this wrong meant an entry whose result was verifiably on
        // the server could never clear, and re-wrote that result every 30
        // seconds forever.
        let propagationError: any = null;
        try {
          if (item.ctx.phase === 'pool') {
            await completeTournamentPoolMatch(
              item.ctx.tournamentId,
              item.ctx.matchId,
              item.result,
              tournament,
              true
            );
          } else if (item.ctx.roundIndex !== undefined && item.ctx.matchIndex !== undefined) {
            await completeTournamentPlayoffMatch(
              item.ctx.tournamentId,
              item.ctx.roundIndex,
              item.ctx.matchIndex,
              item.result,
              tournament,
              true
            );
          } else {
            removeResult(item.id); // malformed beyond repair
            continue;
          }
        } catch (e: any) {
          propagationError = e;
        }

        const landed = await confirmTournamentMatchSaved(
          item.ctx.tournamentId,
          item.ctx.phase === 'pool' ? 'pool' : 'playoff',
          item.ctx.matchId,
          item.ctx.roundIndex,
          item.ctx.matchIndex,
          item.result.winner
        );
        if (landed) {
          // The result is on the server — the outbox's job is done. Anything
          // that failed after it is a propagation problem, repairable by the
          // organiser (Repair advancement) and flagged by the Monitor.
          if (propagationError) {
            // eslint-disable-next-line no-console
            console.warn(
              'Result landed but propagation failed — advancement/champion may need repair.',
              propagationError
            );
          }
          removeResult(item.id);
          sent++;
        } else {
          bumpAttempt(item.id, propagationError?.message || 'Not confirmed by the server yet.');
        }
      } catch (e: any) {
        bumpAttempt(item.id, e?.message || 'Send failed.');
      }
    }
  } finally {
    flushing = false;
  }
  return { sent, remaining: read().length };
};

const bumpAttempt = (id: string, error: string): void => {
  const items = read().map((i) =>
    i.id === id ? { ...i, attempts: i.attempts + 1, lastError: error } : i
  );
  write(items);
  emit();
};

/**
 * Retry whenever the client reconnects, plus a slow poll as a backstop for the
 * case where the connection was never lost but a single write failed.
 */
export const startOutboxRunner = (): (() => void) => {
  let stopped = false;

  const tryFlush = () => {
    if (stopped || read().length === 0) return;
    flushOutbox().catch(() => {});
  };

  tryFlush(); // anything left over from a previous session

  const unsubConnected = onValue(ref(database, '.info/connected'), (snap) => {
    if (snap.val() === true) tryFlush();
  });

  const timer = setInterval(tryFlush, 30000);

  return () => {
    stopped = true;
    unsubConnected();
    clearInterval(timer);
  };
};
